import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Download,
  Eye,
  Share,
  Trash2,
  FileText,
  Calendar,
  User,
  Users,
  Building,
  CheckCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';

interface ReportHistoryItem {
  id: string;
  name: string;
  type: 'player' | 'team' | 'academy' | 'session' | 'custom';
  format: 'pdf' | 'excel' | 'csv';
  generatedAt: string;
  generatedBy: string;
  status: 'completed' | 'failed' | 'processing';
  fileSize: string;
  downloadCount: number;
  recipients?: string[];
  scheduledRun: boolean;
  parameters?: {
    dateRange?: string;
    players?: string[];
    metrics?: string[];
  };
}

const mockReportHistory: ReportHistoryItem[] = [
  {
    id: '1',
    name: 'Daily Player Summary - January 15',
    type: 'player',
    format: 'pdf',
    generatedAt: '2024-01-15T09:00:00Z',
    generatedBy: 'System (Scheduled)',
    status: 'completed',
    fileSize: '2.4 MB',
    downloadCount: 8,
    recipients: ['players', 'parents'],
    scheduledRun: true,
    parameters: {
      dateRange: '2024-01-14 to 2024-01-15',
      players: ['all'],
      metrics: ['distance', 'consistency', 'timing']
    }
  },
  {
    id: '2',
    name: 'Weekly Team Performance Analysis',
    type: 'team',
    format: 'excel',
    generatedAt: '2024-01-14T17:00:00Z',
    generatedBy: 'Coach Sarah Wilson',
    status: 'completed',
    fileSize: '5.7 MB',
    downloadCount: 3,
    scheduledRun: false,
    parameters: {
      dateRange: '2024-01-08 to 2024-01-14',
      metrics: ['team_avg_distance', 'improvement_rate', 'session_attendance']
    }
  },
  {
    id: '3',
    name: 'Custom Player Analysis - Top Performers',
    type: 'custom',
    format: 'pdf',
    generatedAt: '2024-01-14T14:30:00Z',
    generatedBy: 'Coach Mike Johnson',
    status: 'completed',
    fileSize: '3.1 MB',
    downloadCount: 12,
    scheduledRun: false,
    parameters: {
      dateRange: '2024-01-01 to 2024-01-14',
      players: ['Alex Thompson', 'Sarah Chen', 'Michael Rodriguez'],
      metrics: ['shot_distance', 'technique_score', 'consistency']
    }
  },
  {
    id: '4',
    name: 'Session Effectiveness Report',
    type: 'session',
    format: 'csv',
    generatedAt: '2024-01-14T11:15:00Z',
    generatedBy: 'Coach Sarah Wilson',
    status: 'completed',
    fileSize: '1.2 MB',
    downloadCount: 2,
    scheduledRun: false,
    parameters: {
      dateRange: '2024-01-07 to 2024-01-14',
      metrics: ['session_duration', 'player_engagement', 'skill_improvement']
    }
  },
  {
    id: '5',
    name: 'Monthly Academy Overview',
    type: 'academy',
    format: 'pdf',
    generatedAt: '2024-01-01T10:00:00Z',
    generatedBy: 'System (Scheduled)',
    status: 'completed',
    fileSize: '8.3 MB',
    downloadCount: 15,
    recipients: ['academy_admin', 'stakeholders'],
    scheduledRun: true,
    parameters: {
      dateRange: '2023-12-01 to 2023-12-31',
      metrics: ['overall_performance', 'player_count', 'revenue', 'satisfaction']
    }
  },
  {
    id: '6',
    name: 'Parent Progress Update - Failed',
    type: 'player',
    format: 'pdf',
    generatedAt: '2024-01-13T16:00:00Z',
    generatedBy: 'System (Scheduled)',
    status: 'failed',
    fileSize: '0 MB',
    downloadCount: 0,
    recipients: ['parents'],
    scheduledRun: true,
    parameters: {
      dateRange: '2024-01-06 to 2024-01-13',
      players: ['all'],
      metrics: ['weekly_progress', 'achievements', 'recommendations']
    }
  },
  {
    id: '7',
    name: 'Real-time Session Analysis',
    type: 'session',
    format: 'excel',
    generatedAt: '2024-01-13T18:45:00Z',
    generatedBy: 'Coach Mike Johnson',
    status: 'processing',
    fileSize: '0 MB',
    downloadCount: 0,
    scheduledRun: false,
    parameters: {
      dateRange: '2024-01-13',
      metrics: ['live_performance', 'drill_effectiveness', 'player_feedback']
    }
  }
];

const getTypeIcon = (type: ReportHistoryItem['type']) => {
  switch (type) {
    case 'player': return User;
    case 'team': return Users;
    case 'academy': return Building;
    case 'session': return Calendar;
    case 'custom': return FileText;
  }
};

const getStatusColor = (status: ReportHistoryItem['status']) => {
  switch (status) {
    case 'completed': return 'default';
    case 'failed': return 'destructive';
    case 'processing': return 'secondary';
  }
};

const getStatusIcon = (status: ReportHistoryItem['status']) => {
  switch (status) {
    case 'completed': return CheckCircle;
    case 'failed': return AlertCircle;
    case 'processing': return RefreshCw;
  }
};

export const ReportHistory: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');

  const filteredHistory = mockReportHistory
    .filter(item => statusFilter === 'all' || item.status === statusFilter)
    .filter(item => typeFilter === 'all' || item.type === typeFilter)
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime();
      } else if (sortBy === 'oldest') {
        return new Date(a.generatedAt).getTime() - new Date(b.generatedAt).getTime();
      } else if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      }
      return 0;
    });

  const handleDownload = (reportId: string) => {
    console.log('Download report:', reportId);
    // In real implementation, this would initiate download
  };

  const handleShare = (reportId: string) => {
    console.log('Share report:', reportId);
    // In real implementation, this would open share dialog
  };

  const handleDelete = (reportId: string) => {
    console.log('Delete report:', reportId);
    // In real implementation, this would delete the report
  };

  const handlePreview = (reportId: string) => {
    console.log('Preview report:', reportId);
    // In real implementation, this would open preview
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, 'MMM dd, yyyy HH:mm');
  };

  const formatParameters = (parameters?: ReportHistoryItem['parameters']) => {
    if (!parameters) return null;
    
    const parts = [];
    if (parameters.dateRange) parts.push(`Period: ${parameters.dateRange}`);
    if (parameters.players && parameters.players.length > 0) {
      if (parameters.players.includes('all')) {
        parts.push('Players: All');
      } else {
        parts.push(`Players: ${parameters.players.slice(0, 2).join(', ')}${parameters.players.length > 2 ? ` +${parameters.players.length - 2} more` : ''}`);
      }
    }
    if (parameters.metrics && parameters.metrics.length > 0) {
      parts.push(`Metrics: ${parameters.metrics.length} selected`);
    }
    
    return parts.join(' • ');
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Report History</CardTitle>
            <CardDescription>View and manage previously generated reports</CardDescription>
          </div>
          <div className="flex space-x-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="player">Player</SelectItem>
                <SelectItem value="team">Team</SelectItem>
                <SelectItem value="academy">Academy</SelectItem>
                <SelectItem value="session">Session</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="oldest">Oldest First</SelectItem>
                <SelectItem value="name">Name</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <ScrollArea className="h-96">
          <div className="space-y-3">
            {filteredHistory.map((item) => {
              const TypeIcon = getTypeIcon(item.type);
              const StatusIcon = getStatusIcon(item.status);
              
              return (
                <div key={item.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <TypeIcon className="w-4 h-4 text-blue-600" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="font-medium truncate">{item.name}</h4>
                          <Badge variant={getStatusColor(item.status)} className="flex items-center space-x-1">
                            <StatusIcon className="w-3 h-3" />
                            <span>{item.status}</span>
                          </Badge>
                          {item.scheduledRun && (
                            <Badge variant="outline" className="text-xs">
                              Scheduled
                            </Badge>
                          )}
                        </div>
                        
                        <div className="text-sm text-muted-foreground space-y-1">
                          <div className="flex items-center space-x-4">
                            <span>By: {item.generatedBy}</span>
                            <span>•</span>
                            <span>{formatDate(item.generatedAt)}</span>
                            <span>•</span>
                            <span>{item.format.toUpperCase()}</span>
                            <span>•</span>
                            <span>{item.fileSize}</span>
                          </div>
                          
                          {item.parameters && (
                            <div className="text-xs">
                              {formatParameters(item.parameters)}
                            </div>
                          )}
                          
                          {item.recipients && item.recipients.length > 0 && (
                            <div className="text-xs">
                              Recipients: {item.recipients.join(', ')}
                            </div>
                          )}
                          
                          <div className="flex items-center space-x-4 text-xs">
                            <span>Downloads: {item.downloadCount}</span>
                            {item.status === 'processing' && (
                              <span className="flex items-center space-x-1">
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                <span>Processing...</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      {item.status === 'completed' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePreview(item.id)}
                            className="h-8 w-8 p-0"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownload(item.id)}
                            className="h-8 w-8 p-0"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleShare(item.id)}
                            className="h-8 w-8 p-0"
                          >
                            <Share className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {item.status === 'failed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => console.log('Retry report:', item.id)}
                          className="h-8 w-8 p-0"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(item.id)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {filteredHistory.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No reports found matching your filters</p>
              </div>
            )}
          </div>
        </ScrollArea>
        
        {mockReportHistory.length > 10 && (
          <div className="flex justify-center pt-4 border-t">
            <Button variant="outline" size="sm">
              Load More Reports
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};